import React from "react";
import { Box, Card, CardContent, Chip, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PeriodSelection } from "../types";
import { addMonths, getTodayInTZ, MONTH_NAMES, todayYmd, ymd } from "../utils/format";

export type RangePreset = "thisYear" | "allTime" | "custom";

interface Props {
  period: PeriodSelection;
  rangePreset: RangePreset | null;
  onChange: (period: PeriodSelection, preset: RangePreset | null) => void;
}

/**
 * [ Monthly | This Year | All Time | Custom ]   [ ‹ ] [ Month ] [ Year ] [ › ]
 *
 * "Monthly" is the default view (the monthly financial report). The other
 * chips keep the Balance Sheet's original date-range views.
 */
const PeriodToolbar: React.FC<Props> = ({ period, rangePreset, onChange }) => {
  const today = getTodayInTZ();
  const yearOptions: number[] = [];
  for (let y = today.year + 1; y >= today.year - 6; y--) yearOptions.push(y);

  const goMonth = (year: number, month: number) => onChange({ mode: "month", year, month }, null);

  const choosePreset = (preset: RangePreset) => {
    if (preset === "thisYear") {
      onChange({ mode: "range", startDate: ymd(today.year, 1, 1), endDate: todayYmd() }, preset);
    } else if (preset === "allTime") {
      onChange({ mode: "range", startDate: "", endDate: "" }, preset);
    } else {
      const start = period.mode === "month" ? ymd(period.year, period.month, 1) : period.startDate;
      onChange({ mode: "range", startDate: start, endDate: period.mode === "range" ? period.endDate : todayYmd() }, preset);
    }
  };

  const chip = (label: string, active: boolean, onClick: () => void) => (
    <Chip
      label={label}
      clickable
      size="small"
      onClick={onClick}
      color={active ? "primary" : "default"}
      variant={active ? "filled" : "outlined"}
    />
  );

  return (
    <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", "&:last-child": { pb: 2 } }}>
        <Box display="flex" gap={1} flexWrap="wrap">
          {chip("Monthly", period.mode === "month", () =>
            goMonth(period.mode === "month" ? period.year : today.year, period.mode === "month" ? period.month : today.month)
          )}
          {chip("This Year", rangePreset === "thisYear", () => choosePreset("thisYear"))}
          {chip("All Time", rangePreset === "allTime", () => choosePreset("allTime"))}
          {chip("Custom", rangePreset === "custom", () => choosePreset("custom"))}
        </Box>

        {period.mode === "month" ? (
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: { md: "auto" } }}>
            <Tooltip title="Previous month">
              <IconButton
                size="small"
                onClick={() => {
                  const p = addMonths(period.year, period.month, -1);
                  goMonth(p.year, p.month);
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <TextField
              select
              size="small"
              label="Month"
              value={period.month}
              onChange={(e) => goMonth(period.year, Number(e.target.value))}
              sx={{ width: 140 }}
            >
              {MONTH_NAMES.map((name, i) => (
                <MenuItem key={name} value={i + 1}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Year"
              value={period.year}
              onChange={(e) => goMonth(Number(e.target.value), period.month)}
              sx={{ width: 100 }}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Next month">
              <IconButton
                size="small"
                onClick={() => {
                  const n = addMonths(period.year, period.month, 1);
                  goMonth(n.year, n.month);
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ ml: { md: "auto" } }}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={period.startDate}
              onChange={(e) => onChange({ ...period, startDate: e.target.value }, "custom")}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={period.endDate}
              onChange={(e) => onChange({ ...period, endDate: e.target.value }, "custom")}
              sx={{ width: 160 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PeriodToolbar;
